'use client';

import { useState } from 'react';
import Image from 'next/image';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

import {
  TAssessmentImageType,
  TAssessmentType,
  TImageMetaPositionAlt,
} from '@northladder/apis';
import { ButtonV1, CameraCaptureAlt } from '@northladder/design-system';
import { Text } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

import { translations } from '../../../translations';
import {
  ITargetAssessmentConfig,
  useRouteURL,
  useTargetAssessmentConfig,
} from '../../hooks';
import { TAssessmentStatus } from '../../types';
import { PreviewAndUpload } from '../../images-upload';

import { AssessmentContent as AssessmentContentAlt } from '../AssessmentContent';

interface IAssessmentContentProps {
  heroImageURL: string;
  title: TTextLocalized;
  subTitle?: TTextLocalized;
  button: {
    label?: TTextLocalized;
    onClick: () => void;
  };
  altButton: {
    leadingText?: TTextLocalized;
    label?: TTextLocalized;
    onClick: () => void;
  };
}

function AssessmentContent({
  heroImageURL,
  title,
  subTitle,
  button,
  altButton,
}: IAssessmentContentProps) {
  return (
    <main className="flex min-h-[calc(100vh-230px)] items-center justify-center">
      <div className="mx-auto my-auto max-w-4xl px-4 pb-8 text-center sm:px-6 lg:px-8">
        <Image
          alt="Front Image"
          className="
            my-10 h-60 w-full object-contain object-center md:h-80
          "
          height={1200}
          src={heroImageURL}
          width={600}
        />
        <div className="mx-auto flex max-w-sm flex-col items-center py-8 md:max-w-2xl">
          <h1
            className="
              text-md text-primary-700 dark:text-primary-400 w-2/3 font-normal
              tracking-tight
            "
          >
            <Text isInjectableHtml text={title} />
          </h1>
          {subTitle ? (
            <p
              className="
                text-primary-900 dark:text-primary-200 mt-4 hidden w-2/3 max-w-xl
                text-sm font-light
              "
            >
              <Text text={subTitle} />
            </p>
          ) : null}
        </div>

        <div className="flex w-full justify-end">
          <ButtonV1
            className="mx-auto my-4 w-auto px-16"
            isEnabled
            isLoading={false}
            label={button.label || 'Yes'}
            onClick={button.onClick}
            type="button"
          />
        </div>

        {altButton ? (
          <p className="text-sm font-light text-gray-500 dark:text-gray-400">
            {altButton.leadingText ? (
              <Text text={altButton.leadingText} />
            ) : null}

            <button
              className="form-link-action ms-2"
              onClick={altButton.onClick}
              type="button"
            >
              <Text text={altButton.label || 'Restart Assessment'} />
            </button>
          </p>
        ) : null}
      </div>
    </main>
  );
}

interface IntroAssessBlackOptions {
  onGoTo: (status: TAssessmentStatus) => void;
  onRestart?: () => void;
}

interface IAssessmentIntroProps extends IntroAssessBlackOptions {
  intro: ITargetAssessmentConfig['intro'];
}

function AssessmentIntro({ intro, onGoTo, onRestart }: IAssessmentIntroProps) {
  const { title, imageUrl, ctaText, subCTALabelText, subCTAPreText } = intro;

  const handleClickStart = () => {
    onGoTo('3_INSTRUCTIONS');
  };

  const handleClickRestart = () => {
    onRestart?.();
  };

  return (
    <AssessmentContent
      altButton={{
        leadingText: subCTAPreText,
        label: subCTALabelText,
        onClick: handleClickRestart,
      }}
      button={{
        label: ctaText,
        onClick: handleClickStart,
      }}
      heroImageURL={imageUrl}
      title={title}
    />
  );
}

interface ICaptureImageProps {
  imageQuality: number;
  onGoTo: (status: TAssessmentStatus) => void;
  onCaptureImage: (imageUrl: string) => void;
}

function CaptureImage({
  imageQuality,
  onCaptureImage,
  onGoTo,
}: ICaptureImageProps) {
  const handleDismissCamera = () => {
    onGoTo('3_INSTRUCTIONS');
  };

  const handleUpdateCapturedImage = (imagUrl: string) => {
    onCaptureImage(imagUrl);
  };

  return (
    <div
      className="
        fixed z-[100] flex h-full w-full items-center justify-center
        bg-black/50 py-2
      "
    >
      <main className="flex w-[600px] items-center justify-center px-1">
        <div
          className="
            relative mx-auto my-auto w-full max-w-lg pb-8 text-center
            sm:px-6 lg:px-8 "
        >
          <CameraCaptureAlt
            imageQuality={imageQuality}
            onDismissCamera={handleDismissCamera}
            onUpdateCapturedImage={handleUpdateCapturedImage}
          />
        </div>
      </main>
    </div>
  );
}

interface IInstructionsProps {
  assessmentType: TAssessmentType;
  HeroAnimation: ITargetAssessmentConfig['instructions']['HeroAnimation'];
  instructionsItems: TTextLocalized[];
  onNext: (status: TAssessmentStatus) => void;
  onRestart: () => void;
  subTitle?: TTextLocalized;
  title: TTextLocalized;
  thumbnail: string;
}

function Instructions({
  assessmentType,
  HeroAnimation,
  title,
  subTitle,
  instructionsItems,
  onNext,
  onRestart,
  thumbnail,
}: IInstructionsProps) {
  const { instructions } = translations;

  const handleClick = () => {
    onNext('4_IMAGE_CAPTURE');
  };

  const handleRestart = () => {
    onRestart();
  };

  return (
    <main className="flex min-h-[calc(100vh-230px)] items-center justify-center">
      <div className="mx-auto my-auto max-w-4xl px-4 pb-8 text-center sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-sm flex-col items-center py-8 md:max-w-2xl">
          {assessmentType === 'APPLE' ? (
            <HeroAnimation className="my-10 h-60 w-full object-contain object-center" />
          ) : (
            <Image
              alt="Side of the phone to capture"
              className="
                my-10 h-40 w-full object-contain object-center py-4
              "
              height={1200}
              src={thumbnail}
              width={600}
            />
          )}

          <h1
            className="
              text-md text-primary-700 dark:text-primary-400 font-normal
              tracking-tight md:w-2/3
            "
          >
            <Text isInjectableHtml text={title} />
          </h1>
          {subTitle ? (
            <p
              className="
                text-primary-900 dark:text-primary-200 mt-4 hidden max-w-xl text-sm
                font-light md:w-2/3
              "
            >
              <Text text={subTitle} />
            </p>
          ) : null}
        </div>

        <ol className="mx-auto text-start md:w-2/3">
          {instructionsItems.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index} className="my-2 flex  text-xs md:text-sm">
              <span className="w-[20px] shrink-0">{`${index + 1}.`}</span>
              <Text text={item} />
            </li>
          ))}
        </ol>
        <div className="flex w-full justify-end">
          <ButtonV1
            className="mx-auto my-4 w-auto"
            label={instructions.captureCTA}
            onClick={handleClick}
            type="button"
          />
        </div>
        <button
          className="form-link-action ms-2"
          onClick={handleRestart}
          type="button"
        >
          <Text text={instructions.restartAssessment} />
        </button>
      </div>
    </main>
  );
}

interface IImageUploadSuccessProps {
  assessmentImageType: TAssessmentImageType | TImageMetaPositionAlt;
  imageSrc: string;
  onNext: () => void;
}

function ImageUploadSuccess({
  assessmentImageType,
  imageSrc,
  onNext,
}: IImageUploadSuccessProps) {
  const { uploadSuccess } = translations;

  const { previewAndUpload } = useTargetAssessmentConfig(assessmentImageType);

  const handleOnContinue = () => {
    onNext();
  };
  return (
    <div className="mx-auto flex w-auto min-w-[300px] flex-col justify-center">
      <div
        className="
          mx-auto flex max-w-sm flex-col items-center bg-green-50
          shadow-inner
        "
      >
        <Image
          alt="Captured image preview"
          className="my-4 h-[20rem] w-full object-contain object-center"
          height={1200}
          src={imageSrc}
          width={600}
        />
      </div>
      <div className="mx-auto flex max-w-sm flex-col items-center">
        <CheckCircleIcon className="mt-2 h-12 w-12 text-green-500" />
        <div className="mx-auto mb-4 flex max-w-sm flex-col items-center md:max-w-2xl">
          <h1 className="text-md font-normal tracking-tight text-green-600">
            <Text text={uploadSuccess.title} />
          </h1>
          <p className="w-2/3 max-w-xl text-center text-sm  font-light text-green-700 dark:text-green-400">
            <Text isInjectableHtml text={previewAndUpload.description} />
          </p>
        </div>
      </div>

      <div className="flex w-full justify-between gap-10">
        <ButtonV1
          className="my-4 sm:mx-auto sm:w-auto sm:px-24"
          label={previewAndUpload.ctaText}
          onClick={handleOnContinue}
          type="button"
        />
      </div>
    </div>
  );
}

interface IPreviewNextAssessmentProps {
  assessmentImageType: TAssessmentImageType | TImageMetaPositionAlt;
  onNext: () => void;
  onRestart: () => void;
}

function PreviewNextAssessment({
  assessmentImageType,
  onNext,
  onRestart,
}: IPreviewNextAssessmentProps) {
  const { nextScreenInstructions } =
    useTargetAssessmentConfig(assessmentImageType);

  if (!nextScreenInstructions) {
    return null;
  }

  const handleOnContinue = () => {
    onNext();
  };

  const { ConfirmAnimation, title, ctaText, altCtaText, subCTAPreText } =
    nextScreenInstructions;

  return (
    <AssessmentContentAlt
      altButton={{
        leadingText: subCTAPreText,
        label: altCtaText,
        onClick: onRestart,
      }}
      button={{
        onClick: handleOnContinue,
        label: ctaText,
      }}
      HeroAnimation={ConfirmAnimation}
      title={title}
    />
  );
}

interface IAssessBlackScreenCaptureProps {
  identifier: string;
  imageAssessmentId: TAssessmentImageType | TImageMetaPositionAlt;
}

/**
 * -----------------------------------------------------------------------------
 * Creates functionality for selecting, uploading and previewing the uploaded
 * image.
 */
export function AssessScreenCapture({
  identifier,
  imageAssessmentId,
}: IAssessBlackScreenCaptureProps) {
  const { navigateToRoute, resetNavPath, assessmentType } = useRouteURL();

  const { item0, item1, item2 } = translations.instructionsList;

  const {
    intro,
    instructions,
    previewAndUpload,
    nextScreenInstructions,
    thumbnail,
  } = useTargetAssessmentConfig(imageAssessmentId);

  const [capturedImage, setCapturedImage] = useState('');

  const [assessmentStatus, setAssessmentStatus] =
    useState<TAssessmentStatus>('1_INTRO');

  const handleUpdateAssessmentStatus = (status: TAssessmentStatus) => {
    setAssessmentStatus(status);
  };

  const handleOnCameraCapture = (imageUrl: string) => {
    setCapturedImage(imageUrl);
    setAssessmentStatus('5_PREVIEW_AND_UPLOAD');
  };

  const handleOnNextAssessment = () => {
    navigateToRoute(previewAndUpload.onSuccessGoTo);
  };

  const handleOnSuccessAssessment = () => {
    if (nextScreenInstructions) {
      setAssessmentStatus('7_PREVIEW_NEXT_ASSESSMENT');
    } else {
      navigateToRoute(previewAndUpload.onSuccessGoTo);
    }
  };

  const handleOnNextAfterSuccess = () => {
    if (nextScreenInstructions) {
      navigateToRoute(nextScreenInstructions.screen);
    }
  };

  const handleOnRestartAssessment = () => {
    if (assessmentType === 'APPLE') {
      resetNavPath();
    } else {
      navigateToRoute(previewAndUpload.onSuccessGoTo);
    }
  };

  switch (assessmentStatus) {
    case '3_INSTRUCTIONS': {
      return (
        <Instructions
          assessmentType={assessmentType}
          HeroAnimation={instructions.HeroAnimation}
          instructionsItems={[item0, item1, item2]}
          onNext={handleUpdateAssessmentStatus}
          onRestart={handleOnRestartAssessment}
          thumbnail={thumbnail}
          title={instructions.title}
        />
      );
    }

    case '4_IMAGE_CAPTURE': {
      /**
       * Since the apple images have a maximum size of 1MB, we need to reduce
       * the image quality to 0.9 to ensure the image is less than 1MB.
       */
      const imageQuality = assessmentType === 'APPLE' ? 0.8 : 0.65;

      return (
        <CaptureImage
          imageQuality={imageQuality}
          onCaptureImage={handleOnCameraCapture}
          onGoTo={handleUpdateAssessmentStatus}
        />
      );
    }

    case '5_PREVIEW_AND_UPLOAD': {
      return (
        <PreviewAndUpload
          assessmentImageType={imageAssessmentId}
          identifier={identifier}
          imageSrc={capturedImage}
          onGoTo={handleUpdateAssessmentStatus}
          onNextAssessment={handleOnNextAssessment}
          onRestart={handleOnRestartAssessment}
        />
      );
    }

    case '6_UPLOAD_SUCCESS': {
      return (
        <ImageUploadSuccess
          assessmentImageType={imageAssessmentId}
          imageSrc={capturedImage}
          onNext={handleOnSuccessAssessment}
        />
      );
    }

    case '7_PREVIEW_NEXT_ASSESSMENT': {
      return (
        <PreviewNextAssessment
          assessmentImageType={imageAssessmentId}
          // imageSrc={capturedImage}
          onNext={handleOnNextAfterSuccess}
          onRestart={handleOnRestartAssessment}
        />
      );
    }

    case '1_INTRO':
    default: {
      return (
        <AssessmentIntro
          intro={intro}
          onGoTo={handleUpdateAssessmentStatus}
          onRestart={handleOnRestartAssessment}
        />
      );
    }
  }
}
